import { useCallback, useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';

import Heading from '@atlaskit/heading/heading';

import StatusWarningIcon from '@atlaskit/icon/core/status-warning';

import { auth } from '@atlaskit/outbound-auth-flow-client/auth';

import type { AuthError } from '@atlaskit/outbound-auth-flow-client/error';

import { usePublish } from '@atlaskit/rovo-triggers/main';

import { addRovoParamsToUrl } from '@atlaskit/rovo-triggers/params';

import { getATLContextUrl } from '@atlaskit/atlassian-context/get-atl-context-url';

import { Box } from '@atlaskit/primitives/compiled/box';

import { Inline } from '@atlaskit/primitives/compiled/inline';

import { Stack } from '@atlaskit/primitives/compiled/stack';

import { Text } from '@atlaskit/primitives/compiled/text';

import { token } from '@atlaskit/tokens';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

import { useMessageInstanceEvent } from '@atlassian/message-instance-event-frontend-client/useMessageInstanceEvent';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { CHECKLIST_ITEMS, TOTAL_STEPS } from '../constants';

import {
	ChecklistItemId,
	type ChecklistItem,
	type ChecklistItemCompletedFromTraits,
	type ConnectorProvider,
	type ConnectorRecommendation,
	type RecentJiraProjectInfo,
} from '../types';

import { popConfettiOnScreen } from '../services/confetti';

import { useConnectorRecommendation } from '../services/useConnectorRecommendation';

import { defaultMessages } from '../messages';
import { recordChecklistCompletion, recordChecklistItemAction } from '../user-interactions';

import { ChecklistCard } from './ChecklistCard';

import { ProgressDots } from './ProgressDots';

import { OptionsDropdownMenu } from './OptionsDropdownMenu';

import { type JSX } from 'react/jsx-runtime';

const CHECKLIST_STORAGE_KEY = 'rovo-onboarding-checklist-item-states';

/** Poll briefly — content scripts can attach after first paint. */
const EXTENSION_DETECTION_POLL_MS = 500;

const EXTENSION_DETECTION_MAX_MS = 15_000;

type ChecklistCachedState = {
	itemStates?: Partial<ChecklistItemCompletedFromTraits>;
	connectorRecommendation?: ConnectorRecommendation;
};

function getCachedState(): ChecklistCachedState {
	try {
		const cached = globalThis.window?.localStorage.getItem(CHECKLIST_STORAGE_KEY);
		if (cached) {
			return JSON.parse(cached) as ChecklistCachedState;
		}
	} catch {
		// localStorage may be unavailable (private browsing, storage full, etc.)
	}
	return {};
}

function persistCachedState(update: Partial<ChecklistCachedState>): void {
	try {
		const current = getCachedState();
		globalThis.window?.localStorage.setItem(
			CHECKLIST_STORAGE_KEY,
			JSON.stringify({ ...current, ...update }),
		);
	} catch {
		// Silently ignore localStorage write failures
	}
}

/**
 * Merges completion state from traits with cached state from localStorage.
 * Uses an OR merge: if either source marks an item as completed, it's treated as completed.
 * This ensures that if a user completes an item and refreshes before the trait updates,
 * the cached state preserves their progress.
 */
function getInitialItemStates(
	completionState: ChecklistItemCompletedFromTraits,
): ChecklistItemCompletedFromTraits {
	const { itemStates: cached } = getCachedState();
	if (cached) {
		const merged = { ...completionState };
		for (const key of Object.values(ChecklistItemId)) {
			if (cached[key] === true) {
				merged[key] = true;
			}
		}
		return merged;
	}
	return completionState;
}

function hasRovoExtension(): boolean {
	try {
		return (
			typeof globalThis.window !== 'undefined' &&
			typeof globalThis.window?.rovoExtension?.openChat === 'function'
		);
	} catch {
		return false;
	}
}

const styles = cssMap({
	container: {
		width: '100%',
		paddingTop: token('space.200'),
	},
	headerRow: {
		width: '100%',
		paddingBottom: token('space.050'),
	},
	sectionStyles: {
		display: 'flex',
		width: '100%',
	},
	errorMessageContainer: {
		paddingLeft: token('space.250'),
		paddingRight: token('space.250'),
	},
});

type OnboardingChecklistProps = {
	onDismiss: () => void;
	tenantId: string;
	completionState: ChecklistItemCompletedFromTraits;
	thirdPartyConnectorRecommendations: ConnectorProvider[];
	recentJiraProjectInfo?: RecentJiraProjectInfo;
};

export const OnboardingChecklist = ({
	onDismiss,
	tenantId,
	completionState,
	thirdPartyConnectorRecommendations,
	recentJiraProjectInfo,
}: OnboardingChecklistProps): JSX.Element | null => {
	const { messageInstanceViewed } = useMessageInstanceEvent();
	const { onAcknowledged, onCtaActioned } = usePostOfficeUserInteractions();
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const publish = usePublish('ai-mate');
	const { formatMessage } = useIntl();
	const [activeCardIndex, setActiveCardIndex] = useState(0);
	const { isProjectAdmin, jiraProjectKey, jiraProjectWebUrl } = recentJiraProjectInfo ?? {
		isProjectAdmin: false,
		jiraProjectKey: '',
		jiraProjectWebUrl: '',
	};

	const fireConnectorRecommendationErrorEvent = useCallback(
		(error: string) => {
			fireAnalyticsEvent({
				action: 'failed',
				actionSubject: 'thirdPartyOAuth',
				eventType: 'track',
				attributes: {
					oAuthSource: 'rovo-expansion-onboarding-checklist',
					error,
				},
				tags: ['fabricElements'],
			});
		},
		[fireAnalyticsEvent],
	);

	const cachedConnectorRecommendation = useMemo(
		() => getCachedState().connectorRecommendation ?? null,
		[],
	);

	const connectorToRecommendFromHook = useConnectorRecommendation({
		connectorProviders: thirdPartyConnectorRecommendations,
		tenantId,
		fireConnectorRecommendationErrorEvent,
		skip: cachedConnectorRecommendation !== null,
	});

	// Persist the connector recommendation to localStorage so that the app name
	// remains available for rendering even after the user completes OAuth (when the
	// hook returns null) and across page refreshes.
	useEffect(() => {
		if (connectorToRecommendFromHook) {
			persistCachedState({ connectorRecommendation: connectorToRecommendFromHook });
		}
	}, [connectorToRecommendFromHook]);

	const connectorToRecommend = cachedConnectorRecommendation ?? connectorToRecommendFromHook;

	const [isExtensionInstalled, setIsExtensionInstalled] = useState(() => hasRovoExtension());

	useEffect(() => {
		if (typeof globalThis.window === 'undefined') return;
		if (hasRovoExtension()) {
			setIsExtensionInstalled(true);
			return;
		}
		const intervalId = globalThis.window?.setInterval(() => {
			if (hasRovoExtension()) {
				setIsExtensionInstalled(true);
				globalThis.window?.clearInterval(intervalId);
			}
		}, EXTENSION_DETECTION_POLL_MS);
		const stopId = globalThis.window?.setTimeout(() => {
			globalThis.window?.clearInterval(intervalId);
		}, EXTENSION_DETECTION_MAX_MS);
		return () => {
			globalThis.window?.clearInterval(intervalId);
			globalThis.window?.clearTimeout(stopId);
		};
	}, []);

	const [itemStates, setItemStates] = useState<ChecklistItemCompletedFromTraits | undefined>(
		completionState ? getInitialItemStates(completionState) : undefined,
	);

	// Persist itemStates to localStorage whenever it changes, so that
	// if the user refreshes before the trait updates, we retain their progress.
	useEffect(() => {
		if (itemStates) {
			persistCachedState({ itemStates });
		}
	}, [itemStates]);
	const [oauthError, setOauthError] = useState<'generic' | 'cancelled' | null>(null);

	// Auto-dismiss OAuth error after 5 seconds
	useEffect(() => {
		if (!oauthError) {
			return;
		}
		const timer = setTimeout(() => setOauthError(null), 5000);
		return () => clearTimeout(timer);
	}, [oauthError]);

	const isChromeBrowser =
		typeof navigator !== 'undefined' && (navigator.userAgent || '').indexOf('Chrome') >= 0;

	const numOfTotalSteps = useMemo(() => {
		let total = TOTAL_STEPS;
		// `connectWorkTools` is hidden when there's no connector recommendation
		if (!connectorToRecommend) {
			total -= 1;
		}
		// `automateWork` is hidden for non-project-admins (and those who haven't
		// already completed it). Settings/automation pages it links to are
		// admin-only, so we don't show the card to users who can't access them.
		if (!isProjectAdmin || !jiraProjectWebUrl || !jiraProjectKey) {
			total -= 1;
		}
		// `installExtension` is hidden when the extension is already installed or the user is not on Chrome
		if (
			(isExtensionInstalled || !isChromeBrowser) &&
			!itemStates?.[ChecklistItemId.installExtension]
		) {
			total -= 1;
		}
		return total;
	}, [
		connectorToRecommend,
		itemStates,
		isProjectAdmin,
		isExtensionInstalled,
		isChromeBrowser,
		jiraProjectWebUrl,
		jiraProjectKey,
	]);

	// Don't trigger message viewed event if the checklist doesn't have the data to render, if the user has not completed any steps, or if there is an OAuth error.
	useEffect(() => {
		if (
			!messageInstanceViewed ||
			!itemStates ||
			Object.values(itemStates).filter(Boolean).length >= numOfTotalSteps
		) {
			return;
		}

		void messageInstanceViewed({
			numberOfStepsCompleted: Object.values(itemStates).filter(Boolean).length,
			totalSteps: numOfTotalSteps,
		});
	}, [messageInstanceViewed, itemStates, numOfTotalSteps]);
	// Enrich items with dynamic label values, then sort: incomplete first, completed last
	const sortedItems = useMemo(() => {
		if (!itemStates) {
			return [];
		}
		const enrichedItems = CHECKLIST_ITEMS.map((item) => {
			if (item.id === ChecklistItemId.connectWorkTools) {
				if (!connectorToRecommend) {
					return null;
				}
				return { ...item, labelValues: { appName: connectorToRecommend.provider.displayName } };
			}
			if (
				item.id === ChecklistItemId.automateWork &&
				(!isProjectAdmin || !jiraProjectWebUrl || !jiraProjectKey)
			) {
				// The `automateWork` card deep-links to the Jira Automation rule
				// library settings page, which is only accessible to project admins.
				// Hide the card for non-admins or if we are missing project metadata.
				return null;
			}
			if (
				item.id === ChecklistItemId.installExtension &&
				(isExtensionInstalled || !isChromeBrowser)
			) {
				// Keep the card visible if the item was already completed
				if (itemStates[ChecklistItemId.installExtension]) {
					return item as ChecklistItem;
				}
				return null;
			}
			return item as ChecklistItem;
		}).filter((item): item is ChecklistItem => item !== null);
		const incomplete = enrichedItems.filter((item) => !itemStates[item.id]);
		const completed = enrichedItems.filter((item) => itemStates[item.id]);
		return [...incomplete, ...completed];
	}, [
		itemStates,
		connectorToRecommend,
		isProjectAdmin,
		isExtensionInstalled,
		isChromeBrowser,
		jiraProjectWebUrl,
		jiraProjectKey,
	]);

	const onPaginate = useCallback(
		(direction: 'left' | 'right') => {
			fireAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'rovoOnboardingChecklistPagination',
				eventType: 'track',
				attributes: {
					direction,
					totalSteps: numOfTotalSteps,
				},
			});

			setActiveCardIndex((prev) => {
				if (sortedItems.length === 0) {
					return 0;
				}
				const maxIndex = sortedItems.length - 1;
				if (direction === 'left') {
					return Math.max(0, prev - 1);
				}
				return Math.min(maxIndex, prev + 1);
			});
		},
		[fireAnalyticsEvent, numOfTotalSteps, sortedItems.length],
	);

	useEffect(() => {
		setActiveCardIndex((prev) => {
			if (sortedItems.length === 0) {
				return 0;
			}
			return Math.min(prev, sortedItems.length - 1);
		});
	}, [sortedItems]);

	// Only completed (true) item states for the dropdown
	const completedItems = useMemo(() => {
		if (!itemStates) {
			return {} as Partial<ChecklistItemCompletedFromTraits>;
		}
		return Object.fromEntries(
			Object.entries(itemStates).filter(
				(entry): entry is [ChecklistItemId, true] => entry[1] === true,
			),
		) as Partial<ChecklistItemCompletedFromTraits>;
	}, [itemStates]);

	const onClickItem = useCallback(
		async (itemId: ChecklistItemId): Promise<boolean> => {
			switch (itemId) {
				case ChecklistItemId.writeStatusUpdate:
					publish({
						type: 'chat-new',
						source: 'rovo-expansion-onboarding-checklist',
						openChat: true,
						data: {
							prompt: formatMessage(defaultMessages.writeStatusUpdatePrompt),
							dialogues: [],
						},
						openChatMode: 'sidebar',
					});
					return true;
				case ChecklistItemId.connectWorkTools: {
					if (!connectorToRecommend) return true;

					fireAnalyticsEvent({
						action: 'clicked',
						actionSubject: 'thirdPartyOAuth',
						eventType: 'track',
						attributes: {
							oAuthSource: 'rovo-expansion-onboarding-checklist',
							product: connectorToRecommend.provider.providerType.replace(
								'smartlinks-connector-',
								'',
							),
						},
						tags: ['fabricElements'],
					});

					try {
						await auth(connectorToRecommend.outboundAuthUrl);

						fireAnalyticsEvent({
							action: 'completed',
							actionSubject: 'thirdPartyOAuth',
							eventType: 'track',
							attributes: {
								oAuthSource: 'rovo-expansion-onboarding-checklist',
								product: connectorToRecommend.provider.providerType.replace(
									'smartlinks-connector-',
									'',
								),
							},
							tags: ['fabricElements'],
						});

						return true;
					} catch (err) {
						const authErr = err as AuthError;

						// Fire analytics event for failed connection
						fireAnalyticsEvent({
							action: 'failed',
							actionSubject: 'thirdPartyOAuth',
							eventType: 'track',
							attributes: {
								oAuthSource: 'rovo-expansion-onboarding-checklist',
								product: connectorToRecommend.provider.providerType.replace(
									'smartlinks-connector-',
									'',
								),
							},
							tags: ['fabricElements'],
						});

						// Fire analytics event for auth window closed
						if (authErr.type === 'auth_window_closed') {
							setOauthError('cancelled');
						} else {
							setOauthError('generic');
						}

						return false;
					}
				}
				case ChecklistItemId.askRovoFind:
					globalThis.window?.open('https://search.rovo.com', '_blank');
					return true;
				case ChecklistItemId.deepResearch:
					publish({
						type: 'chat-new',
						source: 'rovo-expansion-onboarding-checklist',
						openChat: true,
						data: {
							prompt: formatMessage(defaultMessages.deepResearchPrompt),
							dialogues: [],
							mode: {
								deepResearchEnabled: true,
							},
						},
						openChatMode: 'sidebar',
					});
					return true;
				case ChecklistItemId.promptGallery:
					globalThis.window?.open(
						addRovoParamsToUrl(`${getATLContextUrl('home')}/chat`, {
							pathway: 'chat',
							promptLibraryOpen: true,
						}),
						'_blank',
						'noopener, noreferrer',
					);
					return true;
				case ChecklistItemId.browseAgents:
					publish({
						source: 'rovo-expansion-onboarding-checklist',
						type: 'chat-open',
						data: {
							openAgentSelector: true,
							channelId: 'EMPTY_CONVERSATION_ID',
						},
					});
					return true;
				case ChecklistItemId.automateWork: {
					// Deep-link to the recipient's most recently active project's
					// Automation rule library settings page, e.g.
					//   https://<site>/jira/software/projects/<KEY>/settings/automate#/tab/rule-library
					// When the project key isn't available (best-effort lookup
					// failed), this item wouldn't have been shown to the user and is therefore this code is unreachable
					if (!jiraProjectWebUrl || !jiraProjectKey) return false;
					const baseJiraOrigin = new URL(jiraProjectWebUrl).origin;
					const url = `${baseJiraOrigin}/jira/software/projects/${jiraProjectKey}/settings/automate#/tab/rule-library`;
					globalThis.window?.open(url, '_blank', 'noopener, noreferrer');
					return true;
				}
				case ChecklistItemId.installExtension:
					globalThis.window?.open(
						'https://chromewebstore.google.com/detail/rovo-search-chat-and-more/npbjhobkibekbklkghlhfhieggcggpdb??utm_source=home-onboarding&utm_medium=banner&utm_campaign=rovo_onboarding_in_home&utm_content=rovo_onboarding_in_home_install_cta',
						'_blank',
					);
					return true;
				default:
					throw new Error(`No onClick handler found for itemId: ${itemId as string}`);
			}
		},
		[publish, formatMessage, fireAnalyticsEvent, connectorToRecommend, jiraProjectKey],
	);

	const onItemClick = useCallback(
		async (itemId: ChecklistItemId) => {
			if (!itemStates) {
				return;
			}

			recordChecklistItemAction(onCtaActioned, itemId, itemStates, numOfTotalSteps);

			const success = await onClickItem(itemId);

			if (!success) {
				return;
			}

			fireAnalyticsEvent({
				action: 'completed',
				actionSubject: 'rovoOnboardingChecklistItem',
				eventType: 'track',
				attributes: {
					itemId,
					numberOfStepsCompleted: Object.values(itemStates).filter(Boolean).length + 1,
					totalSteps: numOfTotalSteps,
				},
			});

			setItemStates((prev: ChecklistItemCompletedFromTraits | undefined) => {
				if (!prev) {
					return prev;
				}
				const next = { ...prev, [itemId]: true };
				// If all steps are now completed, fire a completion event, celebrate, and dismiss
				if (Object.values(next).filter(Boolean).length >= numOfTotalSteps) {
					fireAnalyticsEvent({
						action: 'completed',
						actionSubject: 'rovoOnboardingChecklist',
						eventType: 'track',
						attributes: {},
					});

					recordChecklistCompletion(onAcknowledged);

					popConfettiOnScreen();
				} else {
					setActiveCardIndex(0); // reset back to first card
				}

				return next;
			});
		},
		[itemStates, onCtaActioned, onAcknowledged, onClickItem, fireAnalyticsEvent, numOfTotalSteps],
	);

	// Don't render until completionState is available
	if (!itemStates) {
		return null;
	}

	// If all items are completed, render nothing
	if (Object.values(itemStates).filter(Boolean).length >= numOfTotalSteps) {
		return null;
	}

	const activeItem = sortedItems[activeCardIndex];

	return (
		<Box xcss={styles.container} testId="rovo-onboarding-checklist">
			<Stack space="space.100">
				{/* Header */}
				<Inline
					spread="space-between"
					grow="fill"
					alignBlock="start"
					xcss={styles.headerRow}
					shouldWrap
				>
					<Inline space="space.075" alignBlock="center">
						<Heading size="small">
							<Text size="large" weight="bold" maxLines={3}>
								{formatMessage(defaultMessages.title, { totalSteps: numOfTotalSteps })}
							</Text>
						</Heading>
					</Inline>
					{/* Progress and menu button */}
					<Inline space="space.150" alignBlock="center">
						<ProgressDots
							totalSteps={numOfTotalSteps}
							completedSteps={Object.values(itemStates).filter(Boolean).length}
						/>
						<OptionsDropdownMenu onDismiss={onDismiss} completedItems={completedItems} />
					</Inline>
				</Inline>

				{/* Single full-width card (Try it + pagination inside the card) */}
				{activeItem && (
					<Box xcss={styles.sectionStyles} style={{ minWidth: 0 }}>
						<Box
							as="ul"
							role="list"
							style={{
								listStyle: 'none',
								margin: 0,
								padding: 0,
								width: '100%',
								display: 'block',
							}}
						>
							<Box as="li" key={activeItem.id}>
								<ChecklistCard
									checklistItem={activeItem}
									isCompleted={itemStates[activeItem.id]}
									pagination={{
										onPrevious: () => onPaginate('left'),
										onNext: () => onPaginate('right'),
										canGoPrevious: activeCardIndex > 0,
										canGoNext: activeCardIndex < sortedItems.length - 1,
									}}
									onClick={() => onItemClick(activeItem.id)}
								/>
							</Box>
						</Box>
					</Box>
				)}

				{/* OAuth error message */}
				{oauthError && (
					<Box xcss={styles.errorMessageContainer}>
						<Inline space="space.100" alignBlock="center">
							<StatusWarningIcon color={token('color.icon.warning')} label="warning" />
							<Text weight="bold" color="color.text.warning">
								{oauthError === 'cancelled'
									? formatMessage(defaultMessages.oauthErrorCancelled, {
											appName: connectorToRecommend?.provider.displayName,
										})
									: formatMessage(defaultMessages.oauthErrorGeneric, {
											appName: connectorToRecommend?.provider.displayName,
										})}
							</Text>
						</Inline>
					</Box>
				)}
			</Stack>
		</Box>
	);
};
